import type { AppStore } from "@/store/AppStore";
import { AiToolkitCollectionNotFoundError } from "@/modules/ai-toolkit/errors";
import type {
  ListCollectionItemsInput,
  ListCollectionItemsOutput,
} from "@mem-client/ai-toolkit/contract";
import {
  AiToolkitToolName,
  listCollectionItemsInputSchema,
  listCollectionItemsOutputSchema,
} from "@mem-client/ai-toolkit/contract";
import type { AiToolkitToolDefinition } from "@/modules/ai-toolkit/tools/types";
import { buildCollectionItem } from "@/modules/ai-toolkit/tools/collections/collection-tools.helpers";
import {
  compareIsoStringsDescending,
  DEFAULT_AI_TOOLKIT_SEARCH_LIMIT,
} from "@/modules/ai-toolkit/tools/utils/local-search-helpers";

export const listCollectionItems = async ({
  store,
  input,
}: {
  store: AppStore;
  input: ListCollectionItemsInput;
}): Promise<ListCollectionItemsOutput> => {
  const collection = await store.collections.getAsync(input.collectionId);

  if (!collection) {
    throw new AiToolkitCollectionNotFoundError({
      collectionId: input.collectionId,
    });
  }

  const collectionItems = await collection.itemList.getAllCollectionItemsAsync();
  const limitedCollectionItems = collectionItems
    .sort(
      (left, right) =>
        compareIsoStringsDescending({
          left: left.modelData.locally_created_at,
          right: right.modelData.locally_created_at,
        }) || left.id.localeCompare(right.id)
    )
    .slice(0, input.limit ?? DEFAULT_AI_TOOLKIT_SEARCH_LIMIT);

  return {
    items: await Promise.all(
      limitedCollectionItems.map(collectionItem =>
        buildCollectionItem({
          store,
          collectionItem,
          includeNotePreview: input.includeNotePreview ?? false,
          maxContentChars: input.maxContentChars,
        })
      )
    ),
  };
};

export const listCollectionItemsTool: AiToolkitToolDefinition<
  AiToolkitToolName.ListCollectionItems,
  ListCollectionItemsInput,
  ListCollectionItemsOutput
> = {
  name: AiToolkitToolName.ListCollectionItems,
  inputSchema: listCollectionItemsInputSchema,
  outputSchema: listCollectionItemsOutputSchema,
  strict: true,
  execute: listCollectionItems,
};
